import { HttpEvent, HttpHandlerFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Injector, PLATFORM_ID, inject } from '@angular/core';
import { Observable, catchError, finalize, tap, throwError } from 'rxjs';
import { IApiRequestConfig } from '../interfaces/api-request.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../services/auth/auth.service';
import { ApiService } from '../services/api/api.service';
import { SpinnerService } from '../services/spinner/spinner.service';
import { isEmpty } from 'lodash';
import { BrowserStorage } from '../utils/browserStorage';
import { isPlatformBrowser } from '@angular/common';
import { ProjectService } from '../services/project/project.service';
import { TranslateService } from '@ngx-translate/core';

export function authHeaderInterceptor(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
  const platformId = inject(PLATFORM_ID);
  const requestWithAuth = isPlatformBrowser(platformId) ? appendAuthorizationToken(req) : req;
  const requestWithProjectId = appendProjectIdToRequest(requestWithAuth);
  return next(requestWithProjectId);
}

export function errorHandlingInterceptor(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
  const platformId = inject(PLATFORM_ID);
  const apiOptions = inject(ApiService).getOptions();
  const snackBar = isPlatformBrowser(platformId) ? inject(MatSnackBar, { optional: true }) : null;
  const authService = isPlatformBrowser(platformId) ? inject(AuthService, { optional: true }) : null;
  const spinner = isPlatformBrowser(platformId) ? inject(SpinnerService, { optional: true }) : null;
  const injector = inject(Injector);

  return next(req).pipe(
    tap((event) => handleSuccess(event, apiOptions, snackBar)),
    catchError((error) => {
      // Resolve TranslateService lazily (only when an error actually occurs) to avoid a circular
      // dependency: the interceptor runs while TranslateService loads the default language in its
      // constructor, so injecting it synchronously here would trigger NG0200.
      const translateService = isPlatformBrowser(platformId) ? injector.get(TranslateService, null, { optional: true }) : null;
      handleErrorSync(error, apiOptions, req, snackBar, authService, spinner, platformId, translateService);
      return throwError(() => error);
    }),
    finalize(() => {
      // if (spinner) {
      //   spinner.hide();
      // }
    }),
  );
}

function handleErrorSync(
  error: any,
  options: IApiRequestConfig,
  req: HttpRequest<any>,
  snackBar: MatSnackBar | null,
  authService: AuthService | null,
  spinner: SpinnerService | null,
  platformId: Object,
  translateService: TranslateService | null,
): void {
  const errorMessage = getErrorMessage(error);

  if (error?.status === HttpStatusCode.Unauthorized && !req?.url?.includes('auth/signin')) {
    if (spinner) {
      spinner.hide();
    }
    if (snackBar) {
      const sessionExpiredMessage = translateService
        ? translateService.instant('AUTH.SESSION_EXPIRED')
        : 'Tienes que volver a iniciar sesión';
      const closeLabel = translateService ? translateService.instant('COMMON.CLOSE') : 'Cerrar';
      snackBar.open(sessionExpiredMessage, closeLabel, { duration: 5000 });
    }
    if (isPlatformBrowser(platformId) && authService) {
      authService.signOut();
    }
    return;
  }

  if (error && errorMessage && options?.showAPIError && snackBar) {
    if (spinner) {
      spinner.hide();
    }
    const closeLabel = translateService ? translateService.instant('COMMON.CLOSE') : 'Cerrar';
    snackBar.open(errorMessage, closeLabel, { duration: 3000 });
  }
}

function handleSuccess(data: any, options: IApiRequestConfig, snackBar: MatSnackBar | null): void {
  return;
}

function getErrorMessage(e: any): string {
  const errorObject = e?.error?.message;
  if (!errorObject) {
    return 'There was an error';
  }
  if (Array.isArray(errorObject?.errors)) {
    const firstErrorNotNull = errorObject.errors.find((err: any) => !isEmpty(err?.messages));
    if (firstErrorNotNull) {
      if (Array.isArray(firstErrorNotNull?.messages)) {
        return firstErrorNotNull?.messages.length > 0 ? firstErrorNotNull?.messages[0] : 'There was an error';
      }
    }
    return 'There was an error';
  }
  const message = e?.error?.message;
  return message ?? 'There was an error';
}

function appendAuthorizationToken(req: HttpRequest<any>): HttpRequest<any> {
  const token = safeGetToken();
  if (!token) {
    return req;
  }
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function safeGetToken(): string | null {
  try {
    return BrowserStorage.get('userToken');
  } catch {
    return null;
  }
}

function appendProjectIdToRequest(req: HttpRequest<any>): HttpRequest<any> {
  const projectId = inject(ProjectService).getProjectId();
  if (!projectId) {
    return req;
  }

  return req.clone({
    setHeaders: {
      'X-Project-Id': projectId,
    },
  });
}
